<template>
   <transition name="downtime-banner-fade">
      <div
         v-if="showBanner"
         class="downtime-banner-shell"
         role="status"
         aria-live="polite"
      >
         <div class="downtime-banner" dir="rtl">
            <q-icon name="warning_amber" size="22px" />
            <span class="downtime-banner__message" dir="rtl">{{ message }}</span>
            <q-btn
               flat
               dense
               round
               icon="close"
               size="sm"
               class="downtime-banner__close"
               aria-label="بستن اطلاعیه"
               @click="dismissCurrentBanner"
            />
         </div>
      </div>
   </transition>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

type DowntimeBannerStatus = {
   enabled?: boolean;
   message?: string;
   poll_interval_seconds?: number | string;
};

const DEFAULT_MESSAGE =
   'به دلیل اختلالات اینترنت، گپ جی‌پی‌تی موقتا با کندی همراه است. مدت زمان اختلال از بسته شما کسر نخواهد شد. در تلاش برای راه‌اندازی مجدد هستیم.';
const DEFAULT_POLL_INTERVAL_MS = 10 * 1000;
const MIN_POLL_INTERVAL_MS = 10 * 1000;
const MAX_POLL_INTERVAL_MS = 5 * 60 * 1000;
const STATUS_FILE_PATH = '/downtime-banner.json';

const enabled = ref(false);
const message = ref(DEFAULT_MESSAGE);
const dismissedMessage = ref('');
let pollTimeout: number | null = null;
let pollInFlight = false;
let isComponentActive = false;

const showBanner = computed(
   () => enabled.value && message.value !== dismissedMessage.value
);

function normalizePollInterval(rawInterval?: number | string): number {
   const intervalSeconds =
      typeof rawInterval === 'string' ? Number(rawInterval) : rawInterval;

   if (
      intervalSeconds === undefined ||
      !Number.isFinite(intervalSeconds) ||
      intervalSeconds <= 0
   ) {
      return DEFAULT_POLL_INTERVAL_MS;
   }

   return Math.min(
      Math.max(intervalSeconds * 1000, MIN_POLL_INTERVAL_MS),
      MAX_POLL_INTERVAL_MS
   );
}

function scheduleNextPoll(rawInterval?: number | string) {
   if (typeof window === 'undefined' || !isComponentActive) {
      return;
   }

   if (pollTimeout) {
      window.clearTimeout(pollTimeout);
   }

   pollTimeout = window.setTimeout(() => {
      void fetchBannerStatus();
   }, normalizePollInterval(rawInterval));
}

async function fetchBannerStatus() {
   if (!isComponentActive) {
      return;
   }

   if (pollInFlight) {
      scheduleNextPoll();
      return;
   }

   pollInFlight = true;
   let nextPollInterval: number | string | undefined;

   try {
      const response = await fetch(STATUS_FILE_PATH, { cache: 'no-store' });
      if (!response.ok) {
         throw new Error(`downtime_banner_status_${response.status}`);
      }
      const data = (await response.json()) as DowntimeBannerStatus;
      const nextMessage = data?.message || DEFAULT_MESSAGE;

      enabled.value = !!data?.enabled;
      message.value = nextMessage;
      nextPollInterval = data?.poll_interval_seconds;

      if (!enabled.value) {
         dismissedMessage.value = '';
      }
   } catch {
      // Keep the last known banner state if this lightweight poll fails.
   } finally {
      pollInFlight = false;
      scheduleNextPoll(nextPollInterval);
   }
}

function dismissCurrentBanner() {
   dismissedMessage.value = message.value;
}

onMounted(() => {
   isComponentActive = true;
   void fetchBannerStatus();
});

onBeforeUnmount(() => {
   isComponentActive = false;
   if (pollTimeout && typeof window !== 'undefined') {
      window.clearTimeout(pollTimeout);
   }
   pollTimeout = null;
});
</script>

<style scoped lang="scss">
.downtime-banner-shell {
   position: fixed;
   top: max(12px, env(safe-area-inset-top));
   left: 0;
   right: 0;
   z-index: 2500;
   display: flex;
   justify-content: center;
   padding: 0 12px;
   pointer-events: none;
}

.downtime-banner {
   display: flex;
   align-items: center;
   direction: rtl;
   gap: 10px;
   width: fit-content;
   max-width: min(920px, 100%);
   min-height: 46px;
   padding: 11px 14px;
   color: #241400;
   background: linear-gradient(135deg, #ffca28, #ff8f00);
   backdrop-filter: blur(10px);
   border: 1px solid rgba(255, 255, 255, 0.36);
   border-radius: 16px;
   box-shadow: 0 14px 34px rgba(53, 31, 0, 0.22);
   font-size: 13.5px;
   font-weight: 600;
   line-height: 1.7;
   pointer-events: auto;
}

.downtime-banner__message {
   flex: 0 1 auto;
   min-width: 0;
   direction: rtl;
   text-align: start;
   unicode-bidi: plaintext;
}

.downtime-banner__close {
   flex-shrink: 0;
   color: #241400;
}

.downtime-banner-fade-enter-active,
.downtime-banner-fade-leave-active {
   transition: opacity 160ms ease, transform 160ms ease;
}

.downtime-banner-fade-enter-from,
.downtime-banner-fade-leave-to {
   opacity: 0;
   transform: translateY(-8px);
}

@media (max-width: 600px) {
   .downtime-banner-shell {
      top: max(8px, env(safe-area-inset-top));
      padding: 0 8px;
   }

   .downtime-banner {
      align-items: flex-start;
      padding: 10px 12px;
      border-radius: 14px;
      font-size: 12.5px;
      line-height: 1.55;
   }
}
</style>
